export interface TOperatorOption {
    name: string;
    label: string;
}

export class OperatorManager {
    // Base operators available for all types
    private static readonly baseOperators: TOperatorOption[] = [
        { name: "eq", label: "=" },
        { name: "neq", label: "!=" },
        { name: "is_set", label: "Is set" },
        { name: "is_not_set", label: "Is not set" },
    ];

    // Comparison operators for numbers and dates
    private static readonly comparisonOperators: TOperatorOption[] = [
        { name: "gt", label: ">" },
        { name: "lt", label: "<" },
        { name: "gte", label: ">=" },
        { name: "lte", label: "<=" },
        { name: "between", label: "Between" },
    ];

    // String-specific operators
    private static readonly stringOperators: TOperatorOption[] = [
        { name: "contains", label: "Contains" },
        { name: "not_contains", label: "Not contains" },
        { name: "starts_with", label: "Starts with" },
        { name: "ends_with", label: "Ends with" },
    ];

    /**
     * Get operators available for number data type
     */
    static getNumberOperators(): TOperatorOption[] {
        return [...this.baseOperators, ...this.comparisonOperators];
    }

    /**
     * Get operators available for string data type
     */
    static getStringOperators(): TOperatorOption[] {
        return [...this.baseOperators, ...this.stringOperators];
    }

    /**
     * Get operators available for date data type
     */
    static getDateOperators(): TOperatorOption[] {
        return [...this.baseOperators, ...this.comparisonOperators];
    }

    /**
     * Get all available operators
     */
    static getAllOperators(): TOperatorOption[] {
        return [...this.baseOperators, ...this.comparisonOperators, ...this.stringOperators];
    }

    /**
     * Get operator by name
     */
    static getOperatorByName(name: string): TOperatorOption | undefined {
        return this.getAllOperators().find((op) => op.name === name);
    }

    /**
     * Convert operator symbol to name (for backward compatibility)
     */
    static convertSymbolToName(symbol: string): string {
        const symbolToName: Record<string, string> = {
            "=": "eq",
            "!=": "neq",
            ">": "gt",
            "<": "lt",
            ">=": "gte",
            "<=": "lte",
        };
        return symbolToName[symbol] || symbol;
    }

    /**
     * Convert operator name to symbol (for backward compatibility)
     */
    static convertNameToSymbol(name: string): string {
        const nameToSymbol: Record<string, string> = {
            eq: "=",
            neq: "!=",
            gt: ">",
            lt: "<",
            gte: ">=",
            lte: "<=",
        };
        return nameToSymbol[name] || name;
    }

    /**
     * Get base operators that are common for all field types
     * (is_set, is_not_set, eq)
     */
    static getBaseFieldOperators(): TOperatorOption[] {
        return this.baseOperators.filter((op) => ["is_set", "is_not_set", "eq", "neq"].includes(op.name));
    }

    /**
     * Get operators by input type (compatible with filter-modal logic)
     * @param inputType - The field input type ("text", "number", "date")
     * @param includeBase - Whether to include base operators (default: true)
     */
    static getOperatorsByInputType(inputType?: "text" | "date" | "number", includeBase = true): TOperatorOption[] {
        const baseOps = includeBase ? this.getBaseFieldOperators() : [];

        switch (inputType) {
            case "text":
                // Text fields only get base operators by default
                return [...baseOps];

            case "date":
                // Date fields get base operators + between
                return [...baseOps, { name: "between", label: "Between" }];

            case "number":
                // Number fields get base operators + comparison operators
                return [
                    ...baseOps,
                    ...this.comparisonOperators.filter((op) => ["gt", "lt", "gte", "lte"].includes(op.name)),
                ];

            default:
                // Unknown type gets only base operators
                return [...baseOps];
        }
    }

    /**
     * Get operators by field data (compatible with filter-modal Field interface)
     * @param fieldData - Field object with inputType property
     * @param fallbackOperators - Operators to return if fieldData is undefined
     */
    static getOperatorsByFieldData(
        fieldData?: { inputType?: "text" | "date" | "number" },
        fallbackOperators: TOperatorOption[] = [{ name: "eq", label: "=" }],
    ): TOperatorOption[] {
        if (!fieldData) {
            return fallbackOperators;
        }

        return this.getOperatorsByInputType(fieldData.inputType);
    }

    /**
     * Get operators filtered by names
     * @param operatorNames - Array of operator names to include
     */
    static getOperatorsByNames(operatorNames: string[]): TOperatorOption[] {
        return this.getAllOperators().filter((op) => operatorNames.includes(op.name));
    }

    /**
     * Check if operator is valid for input type
     * @param operatorName - The operator name to check
     * @param inputType - The field input type
     */
    static isOperatorValidForInputType(operatorName: string, inputType?: "text" | "date" | "number"): boolean {
        const validOperators = this.getOperatorsByInputType(inputType);
        return validOperators.some((op) => op.name === operatorName);
    }
}
